import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import { CustomerService } from '../services/CustomerService';
import { formatDate } from '../../../shared/utils/formatters';

const ComplaintsSection = ({ mobile, complaints = {}, onUpdate }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newComplaint, setNewComplaint] = useState({ details: '', orderNumber: '' });
    const [loading, setLoading] = useState(false);

    const handleAdd = async () => {
        if (!newComplaint.details.trim()) return;
        setLoading(true);
        try {
            await CustomerService.addComplaint(mobile, {
                ...newComplaint,
                complaintNo: `CMP-${Date.now().toString().slice(-6)}`
            });
            setNewComplaint({ details: '', orderNumber: '' });
            setIsAdding(false);
            onUpdate();
        } catch (error) {
            console.error("Failed to add complaint", error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolve = async (id, currentStatus) => {
        const newStatus = currentStatus === 'Resolved' ? 'Open' : 'Resolved';
        try {
            await CustomerService.updateComplaint(mobile, id, { status: newStatus });
            onUpdate();
        } catch (error) {
            console.error("Failed to update complaint", error);
        }
    };

    const list = Object.entries(complaints || {}).map(([key, val]) => ({ id: key, ...val }));
    list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-50">
                <div className="flex items-center gap-2">
                    <AlertTriangle size={18} className="text-red-500" />
                    <h3 className="font-bold text-slate-800">Complaints History</h3>
                </div>
                <button
                    onClick={() => setIsAdding(!isAdding)}
                    className="text-sm text-purple-600 font-medium hover:underline flex items-center gap-1"
                >
                    {isAdding ? 'Cancel' : 'New Complaint'}
                </button>
            </div>

            {isAdding && (
                <div className="bg-slate-50 p-4 rounded-lg mb-4 border border-slate-200 animate-in fade-in slide-in-from-top-2">
                    <div className="space-y-3">
                        <textarea
                            placeholder="Describe the issue..."
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-100 min-h-[80px]"
                            value={newComplaint.details}
                            onChange={(e) => setNewComplaint({ ...newComplaint, details: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Order Number (Optional)"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-100"
                            value={newComplaint.orderNumber}
                            onChange={(e) => setNewComplaint({ ...newComplaint, orderNumber: e.target.value })}
                        />
                        <div className="flex justify-end">
                            <Button onClick={handleAdd} disabled={loading} size="sm">Submit Complaint</Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {list.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                        <CheckCircle size={32} className="text-green-500 mb-2" />
                        <p className="font-medium text-slate-600">No complaint till now</p>
                        <p className="text-xs">This customer has a clean record.</p>
                    </div>
                ) : (
                    list.map((item) => (
                        <div key={item.id} className={`p-3 rounded-lg border ${item.status === 'Resolved' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-sm text-slate-800">{item.category || 'General'}</span>
                                        <span className="text-xs text-slate-500">#{item.complaintNo}</span>
                                    </div>
                                    <p className="text-sm text-slate-700 mt-1">{item.details}</p>
                                </div>
                                <button
                                    onClick={() => handleResolve(item.id, item.status)}
                                    className={`px-2 py-1 rounded text-xs font-bold border ${item.status === 'Resolved'
                                        ? 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                                        : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
                                        }`}
                                >
                                    {item.status === 'Resolved' ? 'Re-open' : 'Mark Resolved'}
                                </button>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500 mt-2 border-t border-black/5 pt-2">
                                <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {item.createdAt ? formatDate(item.createdAt) : 'Unknown'}
                                </span>
                                {item.orderNumber && <span>Order: {item.orderNumber}</span>}
                                <span className={`flex items-center gap-1 font-medium ${item.status === 'Resolved' ? 'text-green-600' : 'text-red-600'}`}>
                                    {item.status === 'Resolved' ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                                    {item.status}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ComplaintsSection;
